import * as Yup from 'yup';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';

import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Unstable_Grid2';
import CardHeader from '@mui/material/CardHeader';
import LoadingButton from '@mui/lab/LoadingButton';
import LinearProgress from '@mui/material/LinearProgress';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { fCurrencyRupees } from 'src/utils/format-number';

import { useAppDispatcher } from 'src/redux/store';
import { updateLoanAgreement } from 'src/redux/slices/loan-agreement';

import FormProvider, { RHFTextField } from 'src/components/hook-form';

import {
  LoanBorrowerType,
  ILoanAgreementItem,
  IUpdateLoanAgreementPayload,
} from 'src/types/loan-agreement';

import { toCents } from './utils';
import LoanBadDebtAlert from './loan-bad-debt-alert';
import { useBadDebtCheck } from './use-bad-debt-check';
import LoanBorrowerFields, {
  loanBorrowerSchemaShape,
  LoanBorrowerFormValues,
} from './loan-borrower-fields';
import LoanPlanFields, {
  LOAN_PLAN_DEFAULTS,
  toLoanPlanPayload,
  loanPlanSchemaShape,
  LoanPlanFormValues,
} from './loan-plan-fields';

type FormValues = LoanBorrowerFormValues &
  LoanPlanFormValues & {
    capital: number;
    loanPaidDate: Date | null;
    acknowledgeBadDebt: boolean;
  };

type Props = {
  agreement: ILoanAgreementItem;
};

const normalizeNic = (value?: string) => (value ?? '').trim().toUpperCase();
const normalizePhone = (value?: string) => {
  const compact = (value ?? '').replace(/[\s-]/g, '');
  return compact ? `0${compact.replace(/^(?:\+94|94|0)/, '')}` : '';
};
const nameKey = (value?: string) => (value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Admin-only. The agreement's terms are fixed except the capital, and a
 * capital change requires re-entering the plan so the two always match.
 */
export default function LoanAgreementEditForm({ agreement }: Props) {
  const router = useRouter();

  const dispatch = useAppDispatcher();

  const isEmployee = agreement.borrowerType === LoanBorrowerType.EMPLOYEE;

  const methods = useForm<FormValues>({
    resolver: (values, context, options) => {
      const planRequired = toCents(values.capital) !== agreement.capitalInCents;
      const schema = Yup.object().shape({
        ...loanBorrowerSchemaShape,
        ...loanPlanSchemaShape,
        capital: Yup.number()
          .moreThan(0, 'Loan amount is required')
          .test(
            'gte-paid',
            `Capital cannot be less than the ${fCurrencyRupees(
              agreement.paidInCents
            )} already paid`,
            (value) => toCents(value) >= agreement.paidInCents
          ),
        acknowledgeBadDebt: Yup.boolean(),
      });
      // The plan is only validated when the capital changes, which depends
      // on the current values - hence a resolver rebuilt per validation.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return yupResolver(schema as any, { context: { planRequired } })(
        values,
        context,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        options as any
      );
    },
    defaultValues: {
      borrowerType: agreement.borrowerType,
      employee: agreement.employee?._id ?? null,
      borrowerName: agreement.borrowerName ?? '',
      nicNumber: agreement.nicNumber ?? '',
      phoneNumber: agreement.phoneNumber ?? '',
      capital: agreement.capitalInCents / 100,
      loanPaidDate: new Date(agreement.loanPaidDate),
      acknowledgeBadDebt: false,
      ...LOAN_PLAN_DEFAULTS,
      planType: agreement.planType,
      frequency: agreement.frequency ?? LOAN_PLAN_DEFAULTS.frequency,
    },
  });

  const {
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const values = watch();

  const capitalChanged = toCents(values.capital) !== agreement.capitalInCents;

  const changes = useMemo(() => {
    if (isEmployee) {
      return { employee: values.employee !== (agreement.employee?._id ?? null) };
    }
    return {
      borrowerName: nameKey(values.borrowerName) !== nameKey(agreement.borrowerName),
      nicNumber: normalizeNic(values.nicNumber) !== normalizeNic(agreement.nicNumber),
      phoneNumber: normalizePhone(values.phoneNumber) !== normalizePhone(agreement.phoneNumber),
    };
  }, [
    isEmployee,
    values.employee,
    values.borrowerName,
    values.nicNumber,
    values.phoneNumber,
    agreement,
  ]);

  const borrowerChanged = Object.values(changes).some(Boolean);

  const { matches, checking } = useBadDebtCheck(values, {
    excludeAgreement: agreement._id,
    enabled: borrowerChanged,
  });

  const needsAcknowledgement = matches.length > 0 && !values.acknowledgeBadDebt;

  const onSubmit = handleSubmit(async (data) => {
    const body: IUpdateLoanAgreementPayload = {};

    if (isEmployee && 'employee' in changes && changes.employee) {
      body.employee = data.employee as string;
    }
    if (!isEmployee) {
      if ('borrowerName' in changes && changes.borrowerName) {
        body.borrowerName = data.borrowerName.trim();
      }
      // An empty string clears the value on the server.
      if ('nicNumber' in changes && changes.nicNumber) body.nicNumber = data.nicNumber.trim();
      if ('phoneNumber' in changes && changes.phoneNumber) {
        body.phoneNumber = data.phoneNumber.trim();
      }
    }
    if (capitalChanged) {
      body.capitalInCents = toCents(data.capital);
      body.plan = toLoanPlanPayload(data);
    }
    if (matches.length > 0) {
      body.acknowledgeBadDebt = data.acknowledgeBadDebt;
    }

    if (Object.keys(body).length === 0) {
      enqueueSnackbar('Nothing to save', { variant: 'info' });
      return;
    }

    try {
      await dispatch(updateLoanAgreement({ id: agreement._id, agreement: body })).unwrap();
      enqueueSnackbar('Loan agreement updated');
      router.push(paths.dashboard.loans.view(agreement._id));
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message, { variant: 'error' });
    }
  });

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <Stack spacing={3}>
        <Card>
          <CardHeader title="Borrower" />
          {checking && <LinearProgress sx={{ mx: 3, mt: 2, height: 2 }} />}
          <Stack spacing={2} sx={{ p: 3 }}>
            <LoanBorrowerFields showTypeSelector={false} currentEmployee={agreement.employee} />
            <LoanBadDebtAlert matches={matches} isAdmin />
          </Stack>
        </Card>

        <Card>
          <CardHeader
            title="Loan Capital"
            subheader="The agreement terms can't be changed. Changing the capital requires re-entering the installment plan."
          />
          <Grid container spacing={2} sx={{ p: 3 }}>
            <Grid xs={12} md={4}>
              <RHFTextField name="capital" label="Loan Amount (Rupees)" type="number" required />
            </Grid>
          </Grid>

          {capitalChanged && (
            <Stack spacing={2} sx={{ px: 3, pb: 3 }}>
              <Alert severity="info">
                Re-enter the plan for the new capital of {fCurrencyRupees(toCents(values.capital))}.{' '}
                {fCurrencyRupees(agreement.paidInCents)} has already been paid and stays applied to
                the earliest installments.
              </Alert>
              <LoanPlanFields
                capitalInCents={toCents(values.capital)}
                earliestDueDate={values.loanPaidDate}
              />
            </Stack>
          )}
        </Card>

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            component={RouterLink}
            href={paths.dashboard.loans.view(agreement._id)}
            color="inherit"
          >
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={needsAcknowledgement || checking}
          >
            Save Changes
          </LoadingButton>
        </Stack>
      </Stack>
    </FormProvider>
  );
}
